import { Flex, SimpleModal, TextInputField } from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { ContractReference } from 'Cube/domain/cube.domain.types'
import { FC, useState } from 'react'
import {
  getReferenceAt,
  getReferenceModalCopy,
  isDuplicateOtherReference
} from './contractReferences.helpers'

type FormData = {
  name: string
  link: string
}

export const OtherReferenceModal: FC<{
  /** The phase's references, so a duplicate can be caught before saving. */
  references: Array<ContractReference>
  /** Where the reference being changed sits in `references`. */
  editingIndex?: number
  onSubmit: (reference: { name: string; link: string | null }) => void
  onClose: () => void
}> = props => {
  const initialReference = getReferenceAt(props.references, props.editingIndex)
  const copy = getReferenceModalCopy({
    isChanging: Boolean(initialReference),
    noun: 'reference'
  })
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { fields, queries } = useForm<FormData>({
    value: {
      name: initialReference?.value ?? '',
      link: initialReference?.url ?? ''
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [
          required,
          (name: unknown, allValues?: object) => {
            const isDuplicate = isDuplicateOtherReference({
              references: props.references,
              candidate: {
                value: String(name).trim(),
                url: (allValues as FormData | undefined)?.link.trim() || null
              },
              ignoreIndex: props.editingIndex
            })

            return isDuplicate
              ? 'This phase already has a reference with this name and link'
              : undefined
          }
        ]
      },
      { property: 'link' }
    ]
  })

  return (
    <SimpleModal
      isOpen
      onClose={props.onClose}
      variant="dialog"
      minHeight="0px"
      title={copy.title}
      submitButtonText={copy.submitButtonText}
      onSubmit={() => {
        setShowValidationErrors(true)

        if (!queries.isValid) {
          return false
        }

        props.onSubmit({
          name: fields.name.value.trim(),
          link: fields.link.value.trim() || null
        })
        return true
      }}
      data-testid="phase.contractReferences.otherModal"
    >
      <Flex flexDirection="column" gap="16px">
        <TextInputField
          label="Name"
          placeholder="e.g. MSA-2024-001"
          value={fields.name.value}
          onChange={e => fields.name.onChange(e.target.value)}
          validationErrors={fields.name.validationErrors}
          data-testid="phase.contractReferences.otherModal.name"
        />
        <TextInputField
          label="Link (optional)"
          placeholder="e.g. https://example.com/contract"
          value={fields.link.value}
          onChange={e => fields.link.onChange(e.target.value)}
          data-testid="phase.contractReferences.otherModal.link"
        />
      </Flex>
    </SimpleModal>
  )
}
